export type Point = [number, number];

/**
 * Type representing a Bezier curve.
 *
 * @note
 * Bezier represents the four control points between this keyframe and the next
 */
export type BezierData = [Point, Point, Point, Point];

// TODO: it's a temporary placement for this object
/**
 * Predefined curves for easing
 */
export const predefinedCurve: { [id: string]: BezierData[] } = {
	linear: [
		[
			[0, 0],
			[0.5, 0.5],
			[0.5, 0.5],
			[1, 1],
		],
	],
	ease: [
		[
			[0, 0],
			[0.25, 0.1],
			[0.25, 1],
			[1, 1],
		],
	],
	easeIn: [
		[
			[0, 0],
			[0.42, 0],
			[1, 1],
			[1, 1],
		],
	],
	easeOut: [
		[
			[0, 0],
			[0, 0],
			[0.58, 1],
			[1, 1],
		],
	],
	easeInOut: [
		[
			[0, 0],
			[0.42, 0],
			[0.58, 1],
			[1, 1],
		],
	],
	bounceOut: [
		[
			[0, 0],
			[0.1, 0],
			[0.2, 0.5],
			[0.3, 1],
		],
		[
			[0.3, 1],
			[0.4, 0.7],
			[0.6, 0.7],
			[0.7, 1],
		],
		[
			[0.7, 1],
			[0.75, 0.85],
			[0.85, 0.85],
			[0.9, 1],
		],
		[
			[0.9, 1],
			[0.925, 0.95],
			[0.975, 0.95],
			[1, 1],
		],
	],
};



// import { Bezier as BezierData, predefinedCurve } from '../data';

export type Easing = keyof typeof predefinedCurve | null | Bezier;

function BezierFactory(p0x: number, p0y: number, p1x: number, p1y: number, p2x: number, p2y: number, p3x: number, p3y: number) {
	function sampleCurveX(t: number) {
		return Math.pow(1 - t, 3) * p0x + 3 * Math.pow(1 - t, 2) * t * p1x + 3 * (1 - t) * t * t * p2x + t * t * t * p3x;
	}

	function sampleCurveY(t: number) {
		return Math.pow(1 - t, 3) * p0y + 3 * Math.pow(1 - t, 2) * t * p1y + 3 * (1 - t) * t * t * p2y + t * t * t * p3y;
	}

	function sampleCurveDerivativeX(t: number) {
		return 3 * Math.pow(1 - t, 2) * (p1x - p0x) + 6 * (1 - t) * t * (p2x - p1x) + 3 * t * t * (p3x - p2x);
	}

	function solveCurveX(x: number, epsilon: number) {
		let t2 = x;
		let x2 = 0;
		let d2 = 0;
		let i = 0;

		// First try a few iterations of Newton's method -- normally very fast.
		for (t2 = x, i = 0; i < 8; i++) {
			x2 = sampleCurveX(t2) - x;
			if (Math.abs(x2) < epsilon) return t2;
			d2 = sampleCurveDerivativeX(t2);
			if (Math.abs(d2) < 1e-6) break;
			t2 = t2 - x2 / d2;
		}

		// Fall back to the bisection method for reliability.
		let t0 = 0.0;
		let t1 = 1.0;
		t2 = x;

		if (t2 < t0) return t0;
		if (t2 > t1) return t1;

		while (t0 < t1) {
			x2 = sampleCurveX(t2);
			if (Math.abs(x2 - x) < epsilon) return t2;
			if (x > x2) t0 = t2;
			else t1 = t2;
			t2 = (t1 - t0) * 0.5 + t0;
		}

		// Failure.
		return t2;
	}

	return (x: number, epsilon: number) => {
		return sampleCurveY(solveCurveX(x, epsilon));
	};
}

/**
 * Cass to generate and evaluate multiple Bezier curve segments based on provided control points.
 */
export class Bezier {
	private _curveEvaluator: ((t: number, epsilon: number) => number)[] = [];

	/**
	 * Creates an instance of Bezier.
	 * @param _curves An array of control points for each Bezier curve segment.
	 */
	constructor(private _curves: BezierData[]) {
		for (const curve of _curves) {
			this._curveEvaluator.push(BezierFactory(curve[0][0], curve[0][1], curve[1][0], curve[1][1], curve[2][0], curve[2][1], curve[3][0], curve[3][1]));
		}
	}

	evaluate(t: number, epsilon = 1e-6) {
		for (let i = 0; i < this._curves.length; i++) {
			if (t < this._curves[i][3][0]) return this._curveEvaluator[i](t, epsilon);
		}
		return this._curveEvaluator[this._curveEvaluator.length - 1](t, epsilon);
	}
}

const bezierCache = new Map<keyof typeof predefinedCurve, Bezier>();

/**
 * Applies easing to a given time value using pre-defined curves, custom Bezier instances, or returns linear progression if no easing is specified.
 * A cache is implemented to store and reuse Bezier instances for pre-defined easing types to enhance performance.
 */
export function computeEasing(t: number, easing?: Easing): number {
	if (easing === null || easing === undefined) return 0;
	if (easing === 'linear') return t;
	if (easing instanceof Bezier) return easing.evaluate(t);
	if (predefinedCurve[easing]) {
		let bezier = bezierCache.get(easing);
		if (!bezier) {
			bezier = new Bezier(predefinedCurve[easing]);
			bezierCache.set(easing, bezier);
		}
		return bezier.evaluate(t);
	}
	return t;
}
